<template>
	<div class="sk-widget sk-widget-event-details">
		<div>
			<div class="event-details-top" :style="eventBannerStyle"></div>
			<div class="event-details-bottom">
				<h1 class="event-title">
					<div class="ellipsify-text">{{ eventName }}</div>
					<div
						class="pill pill-white last-call"
						v-if="isInPreorderStatus"
					>
						Last Call
						<b>{{ lastCall }}</b>
					</div>
				</h1>
				<div class="event-details-bottom-inner">
					<div>
						<div v-if="eventDescription" class="event-description">
							{{ eventDescription }}
						</div>
						<div class="event-info">
							<div
								class="event-date-pill"
								v-if="isInPreorderStatus"
							>
								<div>{{ eventMonth }}</div>
								<b>{{ eventDay }}</b>
							</div>
							<div class="event-details">
								<div v-if="isInPreorderStatus">
									<i class="sk-icon-clock-solid"></i>
									{{ eventTime }}
								</div>
								<div>
									<i class="sk-icon-utensils-solid"></i>
									{{ eventRestaurantName }}
								</div>
							</div>
						</div>
					</div>
					<div
						class="event-details-bottom-right"
						v-if="isInPreorderStatus"
					>
						<div class="event-reminder">
							<div>
								Not ready to order yet?
								<br />
								Get a convenient reminder before last call!
							</div>
							<button
								class="button button-auto button-secondary-dark"
								@click="reminderClicked"
							>
								<i class="sk-icon-bell-on-solid"></i>
								Remind Me
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.sk-widget-event-details {
	margin-top: 25px;
	margin-bottom: 50px;
}

.sk-widget-event-details > div {
	padding: 0;
}

.event-details-top {
	height: 205px;
	position: relative;
	margin-bottom: -45px;
	z-index: 0;
}

.event-details-bottom {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	padding: 0 20px 20px 20px;
	position: relative;
	z-index: 1;
	background: var(--gR-white);
	border-top-right-radius: 10px;
	border-top-left-radius: 10px;
}

.event-details-bottom-inner {
	display: flex;
	justify-content: space-between;
}

.event-details-bottom-inner > div:first-child {
	max-width: 50%;
}

.event-title {
	margin-left: -20px;
	margin-right: -20px;
	border-top-right-radius: 10px;
	border-top-left-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: 18px;
	font-weight: 600;
	color: var(--gR-base-80);
	margin-bottom: 15px;
	background: var(--gR-base-80);
	height: 45px;
	padding: 0 20px;
}

.event-title > div:not(.last-call) {
	color: var(--gR-white);
	margin-right: 10px;
}

.last-call {
	flex-shrink: 0;
}

.last-call b {
	color: var(--gR-base-80);
}

.event-description {
	color: var(--sk-grey2);
	font-size: 12px;
}

.event-info {
	display: flex;
	align-items: center;
	margin-top: 20px;
	font-size: 11px;
}

.event-details,
.event-details i {
	color: var(--sk-grey3);
}

.event-details i {
	margin-right: 10px;
}

.event-details > div:first-child {
	margin-bottom: 8px;
}

.event-details > div {
	display: flex;
	align-items: center;
}

.event-details-bottom-right {
	display: flex;
	align-items: center;
}

.event-reminder {
	background-color: var(--sk-grey-bg);
	padding: 15px 20px;
	border-radius: 10px;
	display: flex;
	align-items: center;
}

.event-reminder > div:first-child {
	margin-right: 13px;
	font-size: 10px;
	color: var(--sk-grey3);
}

.event-reminder .button {
	font-weight: 400;
	padding: 5px 15px;
	font-size: 10px;
	max-height: 25px;
	flex-shrink: 0;
}

.event-reminder .button i {
	font-size: 10px;
	margin-right: 10px;
}

@media only screen and (max-width: 900px) {
	.sk-widget-event-details {
		margin-left: -10px;
		margin-top: 0;
		width: calc(100% + 20px);
		border-radius: 0;
		background: none;
		box-shadow: none;
	}

	.event-details-bottom > div:first-child {
		max-width: 100%;
	}

	.event-details-bottom-right {
		margin: 12px -20px -20px -20px;
	}

	.event-reminder {
		width: 100%;
		justify-content: space-between;
		border-top-right-radius: 10px;
		border-top-left-radius: 10px;
	}

	.event-details-bottom {
		border-radius: 10px;
		margin: 0 10px 1px 10px;
		box-shadow: 0px 0px 2px rgba(136, 136, 136, 0.25);
	}

	.event-details-bottom-inner {
		flex-direction: column;
	}

	.event-details-bottom-inner > div:first-child {
		max-width: 100%;
	}
}
</style>

<script>
import utils from "@/utils"
import { eventBus, EventBusEvents } from "@/eventBus"
import { NotificationType } from "@/enums/NotificationType"
import eventDetailsMixin from "@/mixins/event-details-mixin"

export default {
	name: "EventDetailsWidget",
	mixins: [eventDetailsMixin],
	props: {
		event: Object
	},
	methods: {
		async reminderClicked() {
			this.$store.dispatch("sendMixpanelEvent", {
				msg: "Event Reminder Clicked"
			})
			const promise = utils.createPromise()
			eventBus.emit(EventBusEvents.OPEN_VERIFY_FLOW, {
				resolve: promise.resolve
			})
			const verified = await promise.promise
			if (!verified) {
				return
			}
			this.$store
				.dispatch("addBusinessEventReminder", this.event.id)
				.then((added) => {
					const message = added
						? "You will receive a reminder before the order cutoff time"
						: "Something went wrong, please try again"
					const type = added
						? NotificationType.SUCCESS
						: NotificationType.ERROR
					eventBus.emit(EventBusEvents.SHOW_NOTIFICATION, {
						message: message,
						type: type
					})
				})
		}
	}
}
</script>
